import { AlertSubTypesEnum, AlertTypesEnum, EntityTypes } from '../../../../utilities/Constants';
import {
    IEntityConversationCreateModel,
    IEntityConversationModel,
    IUpdateAlertUserModel,
} from '../../../../interfaces/Report/IReport';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ChatMessage } from '../../../../components/weekly-recap/ChatMessage';
import CheckmateSelect from '../../../../components/shared/CheckmateSelect';
import { ConfirmationDialog } from '../../../../components/shared/ConfirmationDialog';
import { ICollabModalProps } from './CollabModalContext';
import { IMultiSelectOption } from '../../../../interfaces/ILookup';
import { IUserModel } from '../../../../interfaces/IUser';
import { IValidation } from '../../../../interfaces/IError';
import { IconButton } from '../../../../shared/buttons/IconButton';
import { ResizableDialog } from '../../../../components/shared/ResizableDialog';
import { SignalRMethods } from '../../../../utilities/SignalR';
import Sort from '../../../../stores/Sort';
import { Stack } from 'react-bootstrap';
import classNames from 'classnames';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { uniqueId } from 'lodash';
import { useCloseAlertMutation } from '../../../../components/weekly-recap/useCloseAlertMutation';
import { useCreateEntityConversationMutation } from '../../../../shared/react-query-hooks/mutations/useCreateEntityConversationMutation';
import { useGetEntityConversations } from '../../../../shared/react-query-hooks/useGetEntityConversations';
import { useLoading } from '../../../../contexts/LoadingContext';
import { useSignalRContext } from '../../../../contexts/SignalRContext';

export interface IChatModalData {
    alertGuid?: string;
    actionTargets: IUserModel[];
    showCloseChatActButton: boolean | undefined;
    onCloseChatActs: () => void;
    reportGuid: string;
    reportName: string;
    user: IUserModel;
}

export const ReportChatModal: React.FC<ICollabModalProps<IChatModalData, () => void>> = (props) => {
    if (!props.open || !props.data) return null;

    const { alertHub } = useSignalRContext();

    const entityConversations = useGetEntityConversations(
        EntityTypes.Report,
        props.data.reportGuid
    );

    const loading = useLoading();

    const createMutation = useCreateEntityConversationMutation();
    const closeChatActMutation = useCloseAlertMutation();

    const [actionTargetGuids, setActionTargetGuids] = useState<string[]>([]);
    const [localMessages, setLocalMessages] = useState<IEntityConversationModel[]>([]);
    const [localHasOpenChatAct, setLocalHasOpenChatAct] = useState<boolean>(
        props.data.showCloseChatActButton ?? false
    );
    const [messageText, setMessageText] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [optimisticSavedGuids, setOptimisticSavedGuids] = useState<string[]>([]);
    const [saveErrorGuids, setSaveErrorGuids] = useState<string[]>([]);
    const [showChatActConfirmationDialog, setShowChatActConfirmationDialog] =
        useState<boolean>(false);
    const [validation, setValidation] = useState<IValidation>({});

    const handleActionTargetsChange = (optionsList: IMultiSelectOption[]) => {
        setActionTargetGuids(optionsList.map((opt: IMultiSelectOption) => opt.guid ?? ''));
    };

    const handleMessageChange = (message: string) => {
        setMessageText(message);
    };

    const handleSendClick = () => {
        const tempGuid = uniqueId();
        const newMessage: IEntityConversationModel = {
            actionTargets:
                props.data?.actionTargets.filter((user) =>
                    actionTargetGuids.includes(user.guid ?? '')
                ) ?? [],
            author: props.data?.user ?? ({} as IUserModel),
            dateUTC: new Date().toUTCString(),
            entityGuid: props.data?.reportGuid ?? '',
            entityTypeId: EntityTypes.Report,
            guid: tempGuid,
            text: messageText,
        };

        setOptimisticSavedGuids((prev) => [...prev, tempGuid]);

        const createModel: IEntityConversationCreateModel = {
            entityTypeId: EntityTypes.Report,

            entityGuid: props.data?.reportGuid ?? '',
            actionTargets:
                props.data?.actionTargets
                    .filter((user) => actionTargetGuids.includes(user.guid ?? ''))
                    .map((at) => at.guid!) ?? [],
            message: messageText,
            timestampUTC: new Date().toUTCString(),
        };

        setIsSending(true);
        setMessageText('');
        setActionTargetGuids([]);

        const onSendSuccess = (newConvo: IEntityConversationModel | undefined) => {
            if (newConvo) {
                setIsSending(false);

                setLocalMessages((prev) => {
                    const newConvos = [...prev];
                    const foundIdx = prev.findIndex((msg) => msg.guid === tempGuid);

                    if (foundIdx > -1) {
                        newConvos.splice(foundIdx, 1, newConvo);
                    }

                    return newConvos;
                });
            }
        };

        createMutation
            .mutateAsync(createModel)
            .then(onSendSuccess)
            .catch((error) => {
                showErrorToast(error.message ?? 'Failed to send message');
                setSaveErrorGuids((prev) => [...prev, newMessage?.guid ?? '']);
            })
            .finally(() => {
                setOptimisticSavedGuids((prev) => prev.filter((guid) => guid !== tempGuid));
                setIsSending(false);
            });

        setLocalMessages((prev) => [...prev, newMessage]);
    };

    const handleChatConfirmationsDialogSubmit = async () => {
        if (!props.data?.alertGuid) {
            setValidation((prev) => ({ ...prev, act: ['Failed to close Chat Act'] }));
        }

        const model = {
            alertGuid: props.data?.alertGuid,
            alertType: AlertTypesEnum.Act,
            alertSubType: AlertSubTypesEnum.Chat,
            parentEntityGuid: props.data?.reportGuid,
            parentEntityType: EntityTypes.Report,
        } as IUpdateAlertUserModel;

        loading.showLoading();

        await closeChatActMutation
            .mutateAsync(model)
            .then(() => {
                setShowChatActConfirmationDialog(false);
                setLocalHasOpenChatAct(false);
                props.onClose();
                props.data?.onCloseChatActs();
            })
            .catch(() => {
                showErrorToast(`Failed to close Chat Act for - ${props.data?.reportName}`);
                setShowChatActConfirmationDialog(false);
            })
            .finally(() => {
                loading.hideLoading();
            });
    };

    const handleChatActClick = () => {
        setValidation((prev) => ({ ...prev, act: [] }));
        setShowChatActConfirmationDialog(true);
    };

    // todo jec: pass this in from caller
    const leftFooterComponent = (
        <Stack direction="horizontal" gap={2}>
            <button className="btn btn-no-bg font-orange" onClick={handleChatActClick}>
                <i className="fal fa-check" />
                &nbsp;Chat Act
            </button>
        </Stack>
    );

    useEffect(() => {
        if (entityConversations.isSuccess) {
            setLocalMessages(entityConversations.data ?? []);
        }
    }, [entityConversations.isSuccess, entityConversations.data]);

    useEffect(() => {
        entityConversations.refetch();
    }, []);

    const bindSignalrEvents = () => {
        const handleSendChatMessageReceived = (convo: IEntityConversationModel) => {
            // console.log(`******* ${SignalRMethods.SendChatMessage} *******`, convo);

            if (convo.entityGuid === props.data?.reportGuid) {
                setLocalMessages((prev) => [...prev, convo]);
            }
        };

        if (alertHub !== undefined) {
            console.log(`SignalR - Alert Hub => Listening for ${SignalRMethods.SendChatMessage}`);
            alertHub?.on(SignalRMethods.SendChatMessage, handleSendChatMessageReceived);
        }
    };

    const unbindSignalrEvents = () => {
        console.log(
            `SignalR - Alert Hub => Stopped listening for ${SignalRMethods.SendChatMessage}`
        );
        alertHub?.off(SignalRMethods.SendChatMessage);
    };

    useEffect(() => {
        setLocalHasOpenChatAct(props.data?.showCloseChatActButton ?? false);
    }, [props.data]);

    useEffect(() => {
        bindSignalrEvents();
        return unbindSignalrEvents;
    }, [alertHub]);

    const actionTargetOptions = useMemo(
        () =>
            props.data?.actionTargets
                .filter((user) => user.guid !== props.data?.user.guid)
                .map(
                    (user) =>
                        ({
                            label: `${user.profile?.firstName} ${user.profile?.lastName}`,
                            value: user.guid,
                            guid: user.guid,
                        } as IMultiSelectOption)
                )
                .sort((a, b) => (a.label < b.label ? -1 : 1)),
        [props.data?.actionTargets, props.data?.user]
    );

    const showLeftFooterComponent =
        localHasOpenChatAct && props.data.alertGuid && leftFooterComponent;

    return (
        <>
            <ResizableDialog
                isShowingModal
                title="Chat"
                defaultHeight={600}
                defaultWidth={500}
                minWidth={350}
                bodyComponent={
                    <ChatBodyComponent
                        availableActionTargets={actionTargetOptions ?? []}
                        conversations={localMessages}
                        currentUserGuid={props.data.user.guid!}
                        isSending={isSending}
                        isLoading={entityConversations.isLoading}
                        isLoadingError={entityConversations.isError}
                        loadingError={entityConversations.error}
                        message={messageText}
                        onChangeActionTargets={handleActionTargetsChange}
                        onMessageChange={handleMessageChange}
                        onSendClick={handleSendClick}
                        optimisticSavedGuids={optimisticSavedGuids}
                        saveErrorGuids={saveErrorGuids}
                        selectedActionTargetIds={actionTargetGuids}
                        validation={{}}
                    />
                }
                cancelText="Close"
                handleClose={props.onClose}
                closeButtonClassName="btn btn-default"
                leftFooterComponent={showLeftFooterComponent}
                draggable
            />
            <ConfirmationDialog
                bodyComponent={<div>Are you sure you want to close all of your Chat Acts?</div>}
                cancelText="Cancel"
                onSubmit={handleChatConfirmationsDialogSubmit}
                onCancel={() => setShowChatActConfirmationDialog(false)}
                onClose={() => setShowChatActConfirmationDialog(false)}
                open={showChatActConfirmationDialog}
                submitText="Yes"
                title="Close Your Chat Acts"
            />
        </>
    );
};

interface IChatBodyComponentProps {
    availableActionTargets: IMultiSelectOption[];
    conversations: IEntityConversationModel[];
    currentUserGuid: string;
    isLoading: boolean;
    isLoadingError: boolean;
    isSending: boolean;
    loadingError: unknown;
    message: string;
    onMessageChange: (message: string) => void;
    onSendClick: () => void;
    onChangeActionTargets: (optionsList: any) => void;
    optimisticSavedGuids: string[];
    saveErrorGuids: string[];
    selectedActionTargetIds: string[];
    validation?: IValidation;
}

function ChatBodyComponent(props: IChatBodyComponentProps) {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const actionTargetRef = useRef<HTMLDivElement>(null);
    const messageRef = useRef<HTMLTextAreaElement>(null);

    const [showAddMessage, setShowAddMessage] = useState<boolean>(true);

    const selectedActionTargets = props.availableActionTargets.filter((actionTarget) =>
        props.selectedActionTargetIds.includes(actionTarget.guid ?? '')
    );

    const scrollToBottom = (behavior = 'smooth') => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: behavior as ScrollBehavior });
        }
    };

    const handleAddMessageClick = () => {
        setShowAddMessage(true);

        setTimeout(() => {
            scrollToBottom();

            if (actionTargetRef.current) {
                actionTargetRef.current.focus();
            }
        }, 1);
    };

    const handleCancelAddMessage = () => {
        setShowAddMessage(false);
        props.onChangeActionTargets([]);
        props.onMessageChange('');
    };

    const handleChatReplyClick = (user: IUserModel) => {
        setShowAddMessage(true);

        props.onChangeActionTargets(
            props.availableActionTargets.filter((at) => at.guid === user.guid)
        );

        setTimeout(() => {
            if (messageRef.current) {
                messageRef.current.focus();
            }
        }, 1);
    };

    useEffect(() => {
        if (!props.isLoading) {
            scrollToBottom('instant');
            if (actionTargetRef.current) {
                actionTargetRef.current.focus();
            }
        }
    }, [JSON.stringify(props.conversations), props.isLoading]);

    const messagesStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowY: 'auto',
        padding: '10px',
    };

    const disableSendButton = props.message.length === 0;
    const sendButtonClass = classNames('fa-lg fal fa-share-all', {
        'font-orange': !disableSendButton,
    });

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                padding: '2px',
            }}
        >
            <div className="border-gray" style={messagesStyle}>
                {props.isLoading && <>Loading messages...</>}
                {!props.isLoading && props.conversations.length === 0 && (
                    <div className="text-gray" style={{ opacity: 0.6 }}>
                        No message history...
                    </div>
                )}
                {props.isLoadingError && (
                    <span className="text-danger">Failed to load messages.</span>
                )}
                {props.conversations
                    .sort(Sort.compareDate('dateUTC', undefined, 'asc'))
                    .map((convo: IEntityConversationModel, i: number) => (
                        <ChatMessage
                            actionTargets={props.availableActionTargets}
                            currentUserGuid={props.currentUserGuid}
                            hasError={props.saveErrorGuids.includes(convo?.guid ?? '')}
                            isLastMessage={i === props.conversations.length - 1}
                            key={i}
                            message={convo}
                            onReplyClick={handleChatReplyClick}
                            temporary={props.optimisticSavedGuids.includes(convo?.guid ?? '')}
                        />
                    ))}
                <div ref={messagesEndRef} />
            </div>

            {!showAddMessage && (
                <div className="mt-1">
                    <IconButton
                        buttonCssClass="mr-0 btn-no-bg"
                        icon="fal fa-message"
                        onClick={handleAddMessageClick}
                        text="New Message"
                    />
                </div>
            )}

            {showAddMessage && (
                <div
                    className="fill-light-gray mt-2"
                    style={{ padding: '10px 15px 15px 15px', position: 'relative' }}
                >
                    <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                        <div>New Message</div>
                        <IconButton
                            buttonCssClass="mr-0 btn-no-bg"
                            buttonTitle="Cancel"
                            icon="fal fa-times fa-lg"
                            onClick={handleCancelAddMessage}
                        />
                    </Stack>
                    <Stack direction="horizontal" className="mb-2 mt-2">
                        <div style={{ width: '100%' }}>
                            <CheckmateSelect
                                autoFocus
                                isDisabled={props.isLoading}
                                isMulti
                                onChange={props.onChangeActionTargets}
                                options={props.availableActionTargets}
                                placeholder="Action Target"
                                value={selectedActionTargets}
                                selectRef={actionTargetRef}
                            />
                        </div>
                        <div style={{ width: 0 }} />
                    </Stack>
                    <div className="border-gray" style={{ display: 'flex' }}>
                        <textarea
                            className="form-control only-right-border"
                            disabled={props.isLoading}
                            maxLength={30000}
                            onChange={(e) => props.onMessageChange(e.target.value)}
                            placeholder="type text here..."
                            ref={messageRef}
                            rows={4}
                            value={props.message}
                            style={{ opacity: props.isLoading ? 0.6 : 1 }}
                        />
                        <button
                            className="btn btn-no-bg"
                            onClick={props.onSendClick}
                            disabled={disableSendButton}
                            style={{ width: 50 }}
                        >
                            <i className={sendButtonClass} />
                        </button>
                    </div>
                    <div className="text-danger">{props.validation?.actionTargets}</div>
                </div>
            )}
        </div>
    );
}
